import { useEffect, useState, useRef, Children, cloneElement } from "react";
import ReactDOM from "react-dom";
import useScrollStore from "@/services/useScrollStore";

interface Props {
  children: React.ReactElement;
  calledFromPreview?: boolean;
  calledFromConversations?: boolean;
  setReadPdf?: React.Dispatch<React.SetStateAction<boolean>>;
  setReadPdfText?: React.Dispatch<React.SetStateAction<any>>;
  dataSource?: any;
  responseLoader?: boolean;
  forwardedRef?: React.Ref<HTMLElement>;
  isRTL?: boolean;
}

export default function Sources({
  children,
  calledFromPreview = false,
  calledFromConversations = true,
  setReadPdf = () => {},
  setReadPdfText = () => {},
  dataSource = [],
  responseLoader = false,
  forwardedRef,
  isRTL = false,
}: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    opacity: 0,
    placement: "bottom" as "top" | "bottom" | "left" | "right",
    arrowLeft: 68,
    arrowTop: -9,
  });
  const setIsScrollSource = useScrollStore((state) => state.setIsScrollSource);
  //   let sample  = [{
  //     id: 1,
  //     name: "Introduction to AI",
  //     type: "link",
  //     link: "https://example.com/ai-intro",
  //     logo: "https://cdn-icons-png.flaticon.com/512/3098/3098051.png",
  //   },
  //   {
  //     id: 2,
  //     name: "Getting Started Guide.pdf",
  //     type: "pdf",
  //     content: "This is a sample PDF content string for reading inline.",
  //     logo: "", // no logo provided — should fallback to PDF icon
  //   },
  //   {
  //     id: 3,
  //     name: "Project Summary",
  //     type: "text",
  //     content: "Here's a summary of the key project deliverables and outcomes.",
  //     logo: "", // no logo — should fallback to default text icon
  //   },
  //   {
  //     id: 4,
  //     name: "Official Docs",
  //     type: "link",
  //     link: "https://example.com/docs",
  //     logo: "https://cdn-icons-png.flaticon.com/512/2541/2541991.png",
  //   },
  //   {
  //     id: 2,
  //     name: "Getting Started Guide.pdf",
  //     type: "pdf",
  //     content: "This is a sample PDF content string for reading inline.",
  //     logo: "", // no logo provided — should fallback to PDF icon
  //   },
  //   {
  //     id: 3,
  //     name: "Project Summary",
  //     type: "text",
  //     content: "Here's a summary of the key project deliverables and outcomes.",
  //     logo: "", // no logo — should fallback to default text icon
  //   },
  //   {
  //     id: 4,
  //     name: "Official Docs",
  //     type: "link",
  //     link: "https://example.com/docs",
  //     logo: "https://cdn-icons-png.flaticon.com/512/2541/2541991.png",
  //   },
  // ]

  // Hover state management
  const hoverState = useRef({ trigger: false, tooltip: false });
  const hideTimeout = useRef<NodeJS.Timeout | null>(null);

  const showTooltip = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    setIsVisible(true);
  };
  const hideTooltip = () => {
    hideTimeout.current = setTimeout(() => {
      if (!hoverState.current.trigger && !hoverState.current.tooltip) {
        setIsVisible(false);
      }
    }, 120); // 120ms delay
  };

  useEffect(() => {
    const updatePosition = () => {
      if (
        isVisible &&
        (forwardedRef ? (forwardedRef as any).current : triggerRef.current) &&
        tooltipRef.current
      ) {
        const triggerElem = forwardedRef
          ? (forwardedRef as any).current
          : triggerRef.current;
        const triggerRect = triggerElem.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const tooltipWidth = tooltipRect.width;
        const tooltipHeight = tooltipRect.height;
        const tooltipMargin = 10;
        const containerPadding = 10;

        let placement: "top" | "bottom" | "left" | "right" = "bottom";
        let top = 0;
        let left = 0;

        const spaceAbove = triggerRect.top;
        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceLeft = triggerRect.left;
        const spaceRight = viewportWidth - triggerRect.right;

        if (spaceBelow > tooltipHeight + tooltipMargin) {
          placement = "bottom";
          top = triggerRect.bottom + tooltipMargin;
          left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        } else if (spaceAbove > tooltipHeight + tooltipMargin) {
          placement = "top";
          top = triggerRect.top - tooltipHeight - tooltipMargin;
          left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        } else if (spaceRight > tooltipWidth + tooltipMargin) {
          placement = "right";
          top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
          left = triggerRect.right + tooltipMargin;
        } else if (spaceLeft > tooltipWidth + tooltipMargin) {
          placement = "left";
          top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
          left = triggerRect.left - tooltipWidth - tooltipMargin;
        } else {
          placement = "bottom";
          top = Math.min(
            triggerRect.bottom + tooltipMargin,
            viewportHeight - tooltipHeight - containerPadding,
          );
          left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        }

        left = Math.max(
          containerPadding,
          Math.min(left, viewportWidth - tooltipWidth - containerPadding),
        );
        top = Math.max(
          containerPadding,
          Math.min(top, viewportHeight - tooltipHeight - containerPadding),
        );

        let arrowLeft = 68;
        let arrowTop = -9;

        if (placement === "bottom" || placement === "top") {
          arrowLeft = triggerRect.left + triggerRect.width / 2 - left;
          arrowLeft = Math.max(10, Math.min(arrowLeft, tooltipWidth - 20));
          arrowTop = placement === "bottom" ? -9 : tooltipHeight - 3;
        } else if (placement === "left" || placement === "right") {
          arrowTop = triggerRect.top + triggerRect.height / 2 - top;
          arrowTop = Math.max(10, Math.min(arrowTop, tooltipHeight - 20));
          arrowLeft = placement === "right" ? -9 : tooltipWidth - 3;
        }

        setPosition({ top, left, opacity: 1, placement, arrowLeft, arrowTop });
      }
    };

    if (isVisible) {
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isVisible, forwardedRef]);

  // Attach ref to trigger
  const trigger = cloneElement(children, {
    ref: forwardedRef || triggerRef,
    onMouseEnter: () => {
      hoverState.current.trigger = true;
      showTooltip();
    },
    onMouseLeave: () => {
      hoverState.current.trigger = false;
      hideTooltip();
    },
    "data-testid": "sources-trigger",
  });

  useEffect(() => {
    setIsScrollSource(isVisible);
  }, [isVisible]);

  useEffect(() => {
    if (
      isVisible &&
      (forwardedRef ? (forwardedRef as any).current : triggerRef.current) &&
      tooltipRef.current
    ) {
      const triggerElem = forwardedRef
        ? (forwardedRef as any).current
        : triggerRef.current;
      const triggerRect = triggerElem.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;
      const tooltipMargin = 10;
      const containerPadding = 10;

      let placement: "top" | "bottom" | "left" | "right" = "bottom";
      let top = 0;
      let left = 0;

      const spaceAbove = triggerRect.top;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceLeft = triggerRect.left;
      const spaceRight = viewportWidth - triggerRect.right;

      if (spaceBelow > tooltipHeight + tooltipMargin) {
        placement = "bottom";
        top = triggerRect.bottom + tooltipMargin;
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
      } else if (spaceAbove > tooltipHeight + tooltipMargin) {
        placement = "top";
        top = triggerRect.top - tooltipHeight - tooltipMargin;
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
      } else if (spaceRight > tooltipWidth + tooltipMargin) {
        placement = "right";
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerRect.right + tooltipMargin;
      } else if (spaceLeft > tooltipWidth + tooltipMargin) {
        placement = "left";
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerRect.left - tooltipWidth - tooltipMargin;
      } else {
        placement = "bottom";
        top = Math.min(
          triggerRect.bottom + tooltipMargin,
          viewportHeight - tooltipHeight - containerPadding,
        );
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
      }

      left = Math.max(
        containerPadding,
        Math.min(left, viewportWidth - tooltipWidth - containerPadding),
      );
      top = Math.max(
        containerPadding,
        Math.min(top, viewportHeight - tooltipHeight - containerPadding),
      );

      let arrowLeft = 68;
      let arrowTop = -9;

      if (placement === "bottom" || placement === "top") {
        arrowLeft = triggerRect.left + triggerRect.width / 2 - left;
        arrowLeft = Math.max(10, Math.min(arrowLeft, tooltipWidth - 20));
        arrowTop = placement === "bottom" ? -9 : tooltipHeight - 3;
      } else if (placement === "left" || placement === "right") {
        arrowTop = triggerRect.top + triggerRect.height / 2 - top;
        arrowTop = Math.max(10, Math.min(arrowTop, tooltipHeight - 20));
        arrowLeft = placement === "right" ? -9 : tooltipWidth - 3;
      }

      setPosition({ top, left, opacity: 1, placement, arrowLeft, arrowTop });
    } else {
      setPosition((prev) => ({ ...prev, opacity: 0 }));
    }
  }, [isVisible, forwardedRef, calledFromConversations]);

  function processContent(content: string) {
    let result = content.trim();
    if (result.at(0) && result.at(0) !== result.at(0)?.toUpperCase()) {
      result = "..." + result;
    }
    if (result.at(-1) !== ".") {
      result = result + "...";
    }

    return result;
  }

  if (!dataSource || dataSource.length === 0) return children;

  const tooltipContent = (
    <div
      ref={tooltipRef}
      onMouseEnter={() => {
        hoverState.current.tooltip = true;
        showTooltip();
      }}
      onMouseLeave={() => {
        hoverState.current.tooltip = false;
        hideTooltip();
      }}
      className={`cbb-sources-tooltip sm-tooltip_text sourceTooltip tooltip-${position.placement}`}
      data-branding-protected="true"
      style={
        {
          position: "fixed",
          top: `${position.top}px`,
          left: `${position.left}px`,
          opacity: position.opacity,
          zIndex: 10000000,
          transition: "opacity 0.2s",
          padding: "12px 6px",
          backgroundColor: "#111",
          borderRadius: "10px",
          color: "white",
          direction: isRTL ? "rtl" : "ltr",
          "--arrow-left": `${position.arrowLeft}px`,
          "--arrow-top": `${position.arrowTop}px`,
        } as React.CSSProperties
      }
      dir={isRTL ? "rtl" : "ltr"}
    >
      <h4 className="cbb-sources-heading">Sources</h4>
      <ul className="cbb-sources-list source-scrollbar">
        {dataSource?.map((source, i) => (
          <li
            key={source.id || i}
            className="cbb-sources-list-item cp flex items-center gap-[4px]"
            onClick={() => {
              if (!source?.content && source?.type === "link") {
                window.open(source.link, "_blank");
              } else {
                setReadPdf(true);
                setReadPdfText({
                  title: source.name,
                  content: processContent(source.content || ""),
                  type: source.type,
                  link: source.link,
                });
              }
            }}
          >
            {source?.logo ? (
              <img
                src={
                  source?.type === "link"
                    ? source?.logo
                    : source?.name?.endsWith(".pdf")
                      ? "https://dlvkyia8i4zmz.cloudfront.net/7lPRLKbZQ5qQ86eGnXky_image_7__3_.svg"
                      : "https://dlvkyia8i4zmz.cloudfront.net/default-text-icon.svg"
                }
                alt="source icon"
                className="cbb-sources-icon h-[16px] w-[16px] flex-shrink-0 rounded-full object-cover object-center"
              />
            ) : source?.type !== "link" ? (
              source?.name?.toLowerCase().endsWith(".pdf") ? (
                <PdfIcon className={"flex-shrink-0"} />
              ) : source?.name?.toLowerCase().endsWith(".xlsx") ? (
                <XlsIcon className={"flex-shrink-0"} />
              ) : source?.name?.toLowerCase().endsWith(".docx") ? (
                <DocIcon className={"flex-shrink-0"} />
              ) : source?.name?.toLowerCase().endsWith(".txt") ? (
                <TxtIcon className={"flex-shrink-0"} />
              ) : source?.name?.toLowerCase()?.endsWith(".csv") ? (
                <CsvIcon className={"flex-shrink-0"} />
              ) : (
                <PdfIcon className={"flex-shrink-0"} />
              )
            ) : (
              <LinkIcon className={"flex-shrink-0"} />
            )}

            <p className="max-w-[200px] truncate">{source?.name}</p>
          </li>
        ))}
      </ul>
    </div>
  );
  const portalTarget =
    document.querySelector(
      ".cbb-full-screen-preview-wrapper, .cbb-embed-preview-wrapper",
    ) || document.body;

  return (
    <>
      {trigger}
      {isVisible &&
        ReactDOM.createPortal(tooltipContent, portalTarget as Element)}
    </>
  );
}

const SourcesIcon = ({ fill }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={15}
    height={16}
    viewBox="0 0 15 16"
    fill="none"
    className="sources_icon"
  >
    <g clipPath="url(#clip0_8_1445)">
      <path
        d="M10.625 6.47217C8.2125 6.47217 6.25 8.43467 6.25 10.8472C6.25 13.2597 8.2125 15.2222 10.625 15.2222C13.0375 15.2222 15 13.2597 15 10.8472C15 8.43467 13.0375 6.47217 10.625 6.47217ZM10.625 13.9722C8.9 13.9722 7.5 12.5722 7.5 10.8472C7.5 9.12217 8.9 7.72217 10.625 7.72217C12.35 7.72217 13.75 9.12217 13.75 10.8472C13.75 12.5722 12.35 13.9722 10.625 13.9722ZM11.5625 9.28467C11.5625 9.80342 11.1438 10.2222 10.625 10.2222C10.1062 10.2222 9.6875 9.80342 9.6875 9.28467C9.6875 8.76592 10.1062 8.34717 10.625 8.34717C11.1438 8.34717 11.5625 8.76592 11.5625 9.28467ZM11.25 11.4722V12.7222C11.25 13.0659 10.9688 13.3472 10.625 13.3472C10.2812 13.3472 10 13.0659 10 12.7222V11.4722C10 11.1284 10.2812 10.8472 10.625 10.8472C10.9688 10.8472 11.25 11.1284 11.25 11.4722ZM5.625 13.3472C5.625 13.6909 5.34375 13.9722 5 13.9722H3.125C1.4 13.9722 0 12.5722 0 10.8472V3.34717C0 1.62217 1.4 0.222168 3.125 0.222168H6.725C7.38125 0.222168 8.025 0.490918 8.49375 0.953418L10.5188 2.97842C10.9375 3.39717 11.1937 3.95342 11.2437 4.54092C11.2688 4.88467 11.0125 5.18467 10.6687 5.21592H10.6187C10.2937 5.21592 10.025 4.96592 9.99375 4.64092V4.59092H8.13125C7.44375 4.59092 6.88125 4.02842 6.88125 3.34092V1.48467C6.83125 1.48467 6.78125 1.47217 6.73125 1.47217H3.125C2.09375 1.47217 1.25 2.31592 1.25 3.34717V10.8472C1.25 11.8784 2.09375 12.7222 3.125 12.7222H5C5.34375 12.7222 5.625 13.0034 5.625 13.3472Z"
        fill={fill}
      />
    </g>
    <defs>
      <clipPath id="clip0_8_1445">
        <rect
          width={15}
          height={15}
          fill="white"
          transform="translate(0 0.222168)"
        />
      </clipPath>
    </defs>
  </svg>
);

const GoTo = () => (
  <svg
    className="fs-0 cp"
    width={15}
    height={15}
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.6111 14.1907H2.83332C2.21002 14.1907 1.62409 13.9479 1.1834 13.5072C0.742705 13.0665 0.5 12.4805 0.5 11.8573V4.07956C0.5 3.45629 0.742733 2.87035 1.18342 2.42966C1.62411 1.98897 2.21005 1.74624 2.83335 1.74624H5.94445C6.37403 1.74624 6.72223 2.09444 6.72223 2.52402C6.72223 2.9536 6.37403 3.3018 5.94445 3.3018H2.83332C2.62555 3.3018 2.43026 3.38271 2.28338 3.52959C2.1365 3.67647 2.05556 3.87179 2.05556 4.07956V11.8573C2.05556 12.0651 2.13648 12.2604 2.28338 12.4073C2.43026 12.5542 2.62555 12.6351 2.83332 12.6351H10.6111C10.8188 12.6351 11.0141 12.5542 11.161 12.4073C11.3079 12.2604 11.3888 12.0651 11.3888 11.8573V8.74623C11.3888 8.31665 11.737 7.96847 12.1666 7.96847C12.5962 7.96847 12.9444 8.31667 12.9444 8.74623V11.8573C12.9444 12.4806 12.7017 13.0665 12.261 13.5073C11.8203 13.9479 11.2343 14.1907 10.6111 14.1907ZM5.94445 9.52398C5.74538 9.52398 5.54633 9.44807 5.39448 9.29619C5.09075 8.99246 5.09075 8.49996 5.39448 8.19623L11.8444 1.74624H9.05555C8.62597 1.74624 8.2778 1.39803 8.2778 0.968457C8.2778 0.538879 8.62597 0.190674 9.05555 0.190674H13.7222C13.9253 0.190441 14.1204 0.269888 14.2655 0.411943L14.2656 0.411997L14.2671 0.413519L14.2676 0.413954L14.2688 0.415149L14.2695 0.415937L14.2704 0.416806L14.2721 0.418491L14.2738 0.420175L14.2746 0.421045L14.2754 0.421833C14.276 0.422347 14.2765 0.422901 14.277 0.42349L14.2786 0.425039L14.2786 0.425093C14.4207 0.570255 14.5001 0.765334 14.4999 0.968429V5.6351C14.4999 6.06467 14.1517 6.41288 13.7221 6.41288C13.2925 6.41288 12.9443 6.06467 12.9443 5.6351V2.8462L6.49428 9.29622C6.34254 9.4481 6.14349 9.52398 5.94445 9.52398Z"
      fill="#FB5F66"
    />
  </svg>
);

const OpenIcon = ({ fill }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={13}
    height={13}
    viewBox="0 0 13 13"
    fill="none"
  >
    <path
      d="M11.0575 10.4285L1.94255 10.4285C1.78131 10.4278 1.62389 10.3793 1.49019 10.2892C1.3565 10.199 1.25254 10.0713 1.19145 9.92205C1.13037 9.77283 1.1149 9.60884 1.14701 9.45083C1.17912 9.29282 1.25736 9.14787 1.37185 9.03433L5.92115 4.48503C5.99694 4.40861 6.08711 4.34796 6.18646 4.30657C6.28581 4.26518 6.39238 4.24387 6.5 4.24387C6.60763 4.24387 6.71419 4.26518 6.81354 4.30657C6.91289 4.34796 7.00306 4.40861 7.07886 4.48503L11.6282 9.03433C11.7426 9.14787 11.8209 9.29282 11.853 9.45083C11.8851 9.60884 11.8696 9.77283 11.8086 9.92205C11.7475 10.0713 11.6435 10.199 11.5098 10.2892C11.3761 10.3793 11.2187 10.4278 11.0575 10.4285Z"
      fill={fill}
    />
  </svg>
);

const DownloadIcon = () => (
  <svg
    className="cp"
    xmlns="http://www.w3.org/2000/svg"
    width={15}
    height={15}
    viewBox="0 0 15 15"
    fill="none"
  >
    <path
      d="M7.50184 0.189941C7.33023 0.189983 7.16566 0.25817 7.04432 0.37951C6.92297 0.500851 6.85479 0.665413 6.85474 0.837014V8.21894L4.38008 5.74435C4.31998 5.68426 4.24864 5.6366 4.17012 5.60409C4.0916 5.57158 4.00745 5.55485 3.92247 5.55485C3.75084 5.55487 3.58624 5.62306 3.46489 5.74443C3.34354 5.86579 3.27538 6.0304 3.27539 6.20202C3.27541 6.37365 3.3436 6.53824 3.46497 6.65958L7.04421 10.2382C7.16561 10.3596 7.33021 10.4277 7.50184 10.4277C7.67347 10.4277 7.83807 10.3596 7.95947 10.2382L11.5387 6.65896C11.6601 6.53761 11.7283 6.37302 11.7283 6.2014C11.7283 6.02977 11.6601 5.86517 11.5388 5.7438C11.4174 5.62244 11.2528 5.55424 11.0812 5.55423C10.9096 5.55422 10.745 5.62238 10.6236 5.74373L8.14893 8.21894V0.837014C8.14889 0.665413 8.0807 0.500851 7.95936 0.37951C7.83801 0.25817 7.67345 0.189983 7.50184 0.189941Z"
      fill="#6D6D6D"
    />
    <path
      d="M1.14709 8.74646C0.975474 8.74646 0.810883 8.81463 0.689529 8.93598C0.568176 9.05733 0.5 9.22192 0.5 9.39353V11.5258C0.500782 12.2321 0.781717 12.9092 1.28117 13.4087C1.78062 13.9081 2.4578 14.189 3.16413 14.1898H11.8355C12.5418 14.189 13.219 13.9081 13.7184 13.4087C14.2179 12.9092 14.4988 12.2321 14.4996 11.5258V9.39353C14.4996 9.30856 14.4829 9.22442 14.4504 9.14591C14.4178 9.0674 14.3702 8.99607 14.3101 8.93598C14.25 8.8759 14.1787 8.82823 14.1002 8.79572C14.0216 8.7632 13.9375 8.74646 13.8525 8.74646C13.7675 8.74646 13.6834 8.7632 13.6049 8.79572C13.5264 8.82823 13.455 8.8759 13.395 8.93598C13.3349 8.99607 13.2872 9.0674 13.2547 9.14591C13.2222 9.22442 13.2054 9.30856 13.2054 9.39353V11.5258C13.205 11.889 13.0606 12.2372 12.8037 12.494C12.5469 12.7508 12.1987 12.8952 11.8355 12.8957H3.16413C2.80092 12.8952 2.45271 12.7508 2.19589 12.494C1.93906 12.2372 1.7946 11.889 1.79419 11.5258V9.39353C1.79421 9.30855 1.77748 9.2244 1.74497 9.14589C1.71246 9.06737 1.6648 8.99603 1.6047 8.93594C1.54461 8.87585 1.47327 8.82819 1.39475 8.79567C1.31623 8.76316 1.23208 8.74644 1.14709 8.74646Z"
      fill="#6D6D6D"
    />
  </svg>
);

export const LinkIcon = ({ className }) => (
  <svg
    className={`fs-0 ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    width={17}
    height={17}
    viewBox="0 0 17 17"
    fill="none"
  >
    <g clipPath="url(#clip0_375_1563)">
      <mask
        id="mask0_375_1563"
        style={{
          maskType: "luminance",
        }}
        maskUnits="userSpaceOnUse"
        x={0}
        y={0}
        width={17}
        height={17}
      >
        <path d="M0.5 0.189697H16.5V16.1897H0.5V0.189697Z" fill="white" />
      </mask>
      <g mask="url(#mask0_375_1563)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.6224 6.5897C11.6704 7.0929 11.7 7.6233 11.7 8.1897C11.7 8.40187 11.6157 8.60535 11.4657 8.75538C11.3157 8.90541 11.1122 8.9897 10.9 8.9897C10.6878 8.9897 10.4843 8.90541 10.3343 8.75538C10.1843 8.60535 10.1 8.40187 10.1 8.1897C10.1 7.6193 10.0688 7.0873 10.016 6.5897H6.9736C6.8624 7.6537 6.8616 8.7257 6.9704 9.7897H8.5C8.71217 9.7897 8.91566 9.87398 9.06569 10.024C9.21571 10.174 9.3 10.3775 9.3 10.5897C9.3 10.8019 9.21571 11.0054 9.06569 11.1554C8.91566 11.3054 8.71217 11.3897 8.5 11.3897H7.2328C7.608 13.0537 8.1944 14.1177 8.5008 14.5897C8.5008 14.5897 9.132 14.6001 9.2416 14.5913C9.45286 14.5776 9.66103 14.6475 9.82118 14.786C9.98133 14.9244 10.0806 15.1203 10.0976 15.3313C10.113 15.5428 10.0438 15.7518 9.9052 15.9123C9.76659 16.0728 9.56991 16.1717 9.3584 16.1873C9.2832 16.1929 8.5 16.1897 8.5 16.1897C4.0888 16.1897 0.5 12.6009 0.5 8.1897C0.5 3.7777 4.0888 0.189697 8.5 0.189697C12.9112 0.189697 16.5 3.7777 16.5 8.1897C16.5 8.4929 16.4712 8.7889 16.4368 9.0825C16.405 9.28727 16.2951 9.47176 16.1301 9.59718C15.9652 9.72261 15.758 9.7792 15.5522 9.75506C15.3464 9.73093 15.1579 9.62795 15.0265 9.46776C14.895 9.30758 14.8307 9.10267 14.8472 8.8961C14.8752 8.6633 14.9 8.4297 14.9 8.1897C14.9 7.6361 14.8216 7.1017 14.6896 6.5897H11.6224ZM8.4904 1.8185C8.1816 2.3033 7.6104 3.3633 7.2392 4.9897H9.7488C9.376 3.3633 8.8008 2.3033 8.4904 1.8185ZM2.3104 9.7897H5.3656C5.31565 9.25889 5.29029 8.72605 5.2896 8.1929C5.2896 7.6569 5.316 7.1225 5.3664 6.5897H2.3104C2.17258 7.11193 2.10188 7.64959 2.1 8.1897C2.102 8.7298 2.1727 9.26744 2.3104 9.7897ZM2.9656 11.3897C3.7556 12.7527 5.01883 13.7779 6.5152 14.2705C6.09835 13.3491 5.79014 12.3823 5.5968 11.3897H2.9656ZM5.5984 4.9897C5.79172 3.99634 6.10049 3.02897 6.5184 2.1073C5.02054 2.59955 3.75602 3.62546 2.9656 4.9897H5.5984ZM11.3896 4.9897H14.0336C13.2399 3.61904 11.9678 2.58995 10.4616 2.1001C10.8835 3.02344 11.195 3.99334 11.3896 4.9897Z"
          fill="#fff"
        />
        <path
          d="M12.6783 15.3466L10.6383 10.8586C10.6047 10.7845 10.5944 10.7019 10.609 10.6218C10.6235 10.5417 10.6621 10.4679 10.7197 10.4104C10.7773 10.3528 10.851 10.3142 10.9311 10.2996C11.0112 10.2851 11.0938 10.2954 11.1679 10.329L15.6559 12.369C15.7248 12.4002 15.7835 12.4503 15.8251 12.5134C15.8667 12.5766 15.8896 12.6503 15.891 12.7259C15.8924 12.8015 15.8724 12.876 15.8332 12.9407C15.794 13.0054 15.7372 13.0577 15.6695 13.0914L14.2759 13.7874C14.1987 13.8263 14.1361 13.8892 14.0975 13.9666L13.4007 15.3602C13.367 15.4279 13.3147 15.4846 13.25 15.5238C13.1853 15.563 13.1109 15.5831 13.0352 15.5816C12.9596 15.5802 12.8859 15.5574 12.8228 15.5158C12.7596 15.4742 12.7095 15.4155 12.6783 15.3466Z"
          fill="#fff"
        />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_375_1563">
        <rect
          width={16}
          height={16}
          fill="white"
          transform="translate(0.5 0.189697)"
        />
      </clipPath>
    </defs>
  </svg>
);

export const PdfIcon = ({ className }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_12315_45399)">
      <path
        d="M3.40039 0.262695H8.60645L13.2373 4.49023V12.5996C13.2373 13.2278 12.7278 13.7373 12.0996 13.7373H3.40039C2.77217 13.7373 2.2627 13.2278 2.2627 12.5996V1.40039C2.2627 0.772167 2.77217 0.262695 3.40039 0.262695Z"
        fill="white"
        stroke="#C8D2E1"
        strokeWidth={0.525}
      />
      <path
        d="M8.7998 0.349609V3.49961C8.7998 4.07951 9.26991 4.54961 9.8498 4.54961H13.5998"
        stroke="#C8D2E1"
        strokeWidth={0.525}
        strokeLinecap="round"
      />
    </g>
    <rect y={6.84961} width={8.75} height={5.4} rx={0.7} fill="#FF1607" />
    <path
      d="M1.2363 10.5498V8.00435H2.24057C2.43363 8.00435 2.59811 8.04122 2.734 8.11497C2.86989 8.18788 2.97346 8.28939 3.04472 8.41948C3.11681 8.54874 3.15285 8.69789 3.15285 8.86692C3.15285 9.03596 3.11639 9.1851 3.04348 9.31436C2.97056 9.44363 2.86491 9.5443 2.72654 9.61639C2.58899 9.68848 2.42244 9.72452 2.22689 9.72452H1.5868V9.29324H2.13989C2.24347 9.29324 2.32881 9.27542 2.39593 9.23979C2.46387 9.20333 2.51442 9.1532 2.54756 9.0894C2.58153 9.02477 2.59852 8.95061 2.59852 8.86692C2.59852 8.7824 2.58153 8.70866 2.54756 8.64569C2.51442 8.58188 2.46387 8.53258 2.39593 8.49778C2.32798 8.46215 2.24181 8.44434 2.1374 8.44434H1.77448V10.5498H1.2363ZM4.33476 10.5498H3.43242V8.00435H4.34222C4.59826 8.00435 4.81866 8.05531 5.00344 8.15723C5.18822 8.25832 5.33032 8.40373 5.42975 8.59348C5.53001 8.78323 5.58014 9.01027 5.58014 9.27459C5.58014 9.53974 5.53001 9.76761 5.42975 9.95819C5.33032 10.1488 5.18739 10.295 5.00095 10.3969C4.81535 10.4988 4.59328 10.5498 4.33476 10.5498ZM3.97059 10.0887H4.31239C4.47148 10.0887 4.6053 10.0605 4.71385 10.0042C4.82322 9.947 4.90525 9.85875 4.95994 9.73944C5.01545 9.61929 5.04321 9.46434 5.04321 9.27459C5.04321 9.0865 5.01545 8.93279 4.95994 8.81348C4.90525 8.69416 4.82363 8.60633 4.71509 8.54998C4.60654 8.49364 4.47272 8.46547 4.31363 8.46547H3.97059V10.0887ZM5.9088 10.5498V8.00435H7.59417V8.44806H6.44698V9.0546H7.48231V9.49831H6.44698V10.5498H5.9088Z"
      fill="white"
    />
    <defs>
      <clipPath id="clip0_12315_45399">
        <rect width={12} height={14} fill="white" transform="translate(2)" />
      </clipPath>
    </defs>
  </svg>
);

export const TxtIcon = ({ className }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_12315_45415)">
      <path
        d="M3.40039 0.262695H8.60645L13.2373 4.49023V12.5996C13.2373 13.2278 12.7278 13.7373 12.0996 13.7373H3.40039C2.77217 13.7373 2.2627 13.2278 2.2627 12.5996V1.40039C2.2627 0.772167 2.77217 0.262695 3.40039 0.262695Z"
        fill="white"
        stroke="#C8D2E1"
        strokeWidth={0.525}
      />
      <path
        d="M8.7998 0.349609V3.49961C8.7998 4.07951 9.26991 4.54961 9.8498 4.54961H13.5998"
        stroke="#C8D2E1"
        strokeWidth={0.525}
        strokeLinecap="round"
      />
    </g>
    <rect y={6.84961} width={9.75} height={5.4} rx={0.7} fill="#344054" />
    <path
      d="M1.48042 8.44806V8.00435H3.57098V8.44806H2.79168V10.5498H2.25972V8.44806H1.48042ZM4.34988 8.00435L4.86319 8.87189H4.88308L5.39888 8.00435H6.00666L5.22985 9.27708L6.02406 10.5498H5.4051L4.88308 9.68102H4.86319L4.34118 10.5498H3.7247L4.5214 9.27708L3.73961 8.00435H4.34988ZM6.18027 8.44806V8.00435H8.27082V8.44806H7.49152V10.5498H6.95956V8.44806H6.18027Z"
      fill="white"
    />
    <defs>
      <clipPath id="clip0_12315_45415">
        <rect width={12} height={14} fill="white" transform="translate(2)" />
      </clipPath>
    </defs>
  </svg>
);

export const DocIcon = ({ className }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_12315_45412)">
      <path
        d="M3.40039 0.262695H8.60645L13.2373 4.49023V12.5996C13.2373 13.2278 12.7278 13.7373 12.0996 13.7373H3.40039C2.77217 13.7373 2.2627 13.2278 2.2627 12.5996V1.40039C2.2627 0.772167 2.77217 0.262695 3.40039 0.262695Z"
        fill="white"
        stroke="#C8D2E1"
        strokeWidth={0.525}
      />
      <path
        d="M8.7998 0.349609V3.49961C8.7998 4.07951 9.26991 4.54961 9.8498 4.54961H13.5998"
        stroke="#C8D2E1"
        strokeWidth={0.525}
        strokeLinecap="round"
      />
    </g>
    <rect y={6.84961} width={9.75} height={5.4} rx={0.7} fill="#3873FF" />
    <path
      d="M2.11057 10.5498H1.20823V8.00435H2.11803C2.37407 8.00435 2.59447 8.05531 2.77925 8.15723C2.96403 8.25832 3.10613 8.40373 3.20556 8.59348C3.30583 8.78323 3.35596 9.01027 3.35596 9.27459C3.35596 9.53974 3.30583 9.76761 3.20556 9.95819C3.10613 10.1488 2.9632 10.295 2.77676 10.3969C2.59116 10.4988 2.36909 10.5498 2.11057 10.5498ZM1.7464 10.0887H2.0882C2.24729 10.0887 2.38111 10.0605 2.48966 10.0042C2.59903 9.947 2.68106 9.85875 2.73575 9.73944C2.79127 9.61929 2.81902 9.46434 2.81902 9.27459C2.81902 9.0865 2.79127 8.93279 2.73575 8.81348C2.68106 8.69416 2.59944 8.60633 2.4909 8.54998C2.38235 8.49364 2.24853 8.46547 2.08944 8.46547H1.7464V10.0887ZM6.02375 9.27708C6.02375 9.55466 5.97113 9.79081 5.8659 9.98553C5.7615 10.1802 5.61898 10.329 5.43834 10.4317C5.25854 10.5336 5.05636 10.5846 4.83181 10.5846C4.6056 10.5846 4.4026 10.5332 4.22279 10.4305C4.04298 10.3277 3.90088 10.179 3.79648 9.98429C3.69207 9.78957 3.63987 9.55383 3.63987 9.27708C3.63987 8.9995 3.69207 8.76335 3.79648 8.56863C3.90088 8.37391 4.04298 8.22559 4.22279 8.12367C4.4026 8.02092 4.6056 7.96955 4.83181 7.96955C5.05636 7.96955 5.25854 8.02092 5.43834 8.12367C5.61898 8.22559 5.7615 8.37391 5.8659 8.56863C5.97113 8.76335 6.02375 8.9995 6.02375 9.27708ZM5.47812 9.27708C5.47812 9.09727 5.45119 8.94564 5.39733 8.82218C5.3443 8.69872 5.26931 8.60508 5.17236 8.54128C5.07542 8.47748 4.9619 8.44558 4.83181 8.44558C4.70172 8.44558 4.5882 8.47748 4.49126 8.54128C4.39431 8.60508 4.31891 8.69872 4.26505 8.82218C4.21202 8.94564 4.1855 9.09727 4.1855 9.27708C4.1855 9.45688 4.21202 9.60852 4.26505 9.73198C4.31891 9.85544 4.39431 9.94907 4.49126 10.0129C4.5882 10.0767 4.70172 10.1086 4.83181 10.1086C4.9619 10.1086 5.07542 10.0767 5.17236 10.0129C5.26931 9.94907 5.3443 9.85544 5.39733 9.73198C5.45119 9.60852 5.47812 9.45688 5.47812 9.27708ZM8.59832 8.89551H8.05394C8.04399 8.82508 8.02369 8.76252 7.99303 8.70783C7.96237 8.65231 7.92302 8.60508 7.87496 8.56614C7.8269 8.5272 7.77138 8.49737 7.70841 8.47665C7.64626 8.45594 7.57873 8.44558 7.50582 8.44558C7.37407 8.44558 7.25931 8.47831 7.16153 8.54377C7.06376 8.6084 6.98794 8.70286 6.93408 8.82715C6.88023 8.95061 6.8533 9.10059 6.8533 9.27708C6.8533 9.45854 6.88023 9.611 6.93408 9.73446C6.98877 9.85792 7.065 9.95114 7.16278 10.0141C7.26055 10.0771 7.37366 10.1086 7.50209 10.1086C7.57418 10.1086 7.64088 10.099 7.70219 10.08C7.76434 10.0609 7.81944 10.0332 7.8675 9.99672C7.91556 9.95943 7.95533 9.91427 7.98682 9.86124C8.01913 9.80821 8.04151 9.74772 8.05394 9.67978L8.59832 9.68226C8.58424 9.79909 8.54902 9.91178 8.49268 10.0203C8.43716 10.128 8.36217 10.2246 8.26771 10.3099C8.17408 10.3944 8.06222 10.4616 7.93213 10.5113C7.80287 10.5602 7.65662 10.5846 7.49339 10.5846C7.26635 10.5846 7.06335 10.5332 6.88437 10.4305C6.70622 10.3277 6.56536 10.179 6.46178 9.98429C6.35904 9.78957 6.30766 9.55383 6.30766 9.27708C6.30766 8.9995 6.35987 8.76335 6.46427 8.56863C6.56867 8.37391 6.71036 8.22559 6.88934 8.12367C7.06832 8.02092 7.26967 7.96955 7.49339 7.96955C7.64088 7.96955 7.7776 7.99026 7.90354 8.03169C8.03032 8.07312 8.1426 8.13361 8.24037 8.21316C8.33814 8.29187 8.41769 8.38841 8.47901 8.50275C8.54115 8.6171 8.58092 8.74802 8.59832 8.89551Z"
      fill="white"
    />
    <defs>
      <clipPath id="clip0_12315_45412">
        <rect width={12} height={14} fill="white" transform="translate(2)" />
      </clipPath>
    </defs>
  </svg>
);

export const XlsIcon = ({ className }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_12315_45386)">
      <path
        d="M3.40039 0.262695H8.60645L13.2373 4.49023V12.5996C13.2373 13.2278 12.7278 13.7373 12.0996 13.7373H3.40039C2.77217 13.7373 2.2627 13.2278 2.2627 12.5996V1.40039C2.2627 0.772167 2.77217 0.262695 3.40039 0.262695Z"
        fill="white"
        stroke="#C8D2E1"
        strokeWidth={0.525}
      />
      <path
        d="M8.7998 0.349609V3.49961C8.7998 4.07951 9.26991 4.54961 9.8498 4.54961H13.5998"
        stroke="#C8D2E1"
        strokeWidth={0.525}
        strokeLinecap="round"
      />
    </g>
    <rect y={6.84961} width={8.75} height={5.4} rx={0.7} fill="#0DB664" />
    <path
      d="M1.78023 8.00435L2.29354 8.87189H2.31343L2.82923 8.00435H3.43701L2.6602 9.27708L3.45441 10.5498H2.83545L2.31343 9.68102H2.29354L1.77153 10.5498H1.15505L1.95175 9.27708L1.16996 8.00435H1.78023ZM3.70756 10.5498V8.00435H4.24574V10.1061H5.337V10.5498H3.70756ZM7.00426 8.73642C6.99432 8.63616 6.95165 8.55827 6.87625 8.50275C6.80084 8.44724 6.69851 8.41948 6.56925 8.41948C6.48142 8.41948 6.40726 8.43191 6.34677 8.45676C6.28628 8.48079 6.23988 8.51435 6.20757 8.55744C6.17608 8.60053 6.16034 8.64941 6.16034 8.7041C6.15868 8.74967 6.16821 8.78945 6.18892 8.82342C6.21047 8.85739 6.23988 8.88681 6.27717 8.91167C6.31446 8.93569 6.35754 8.95682 6.40643 8.97505C6.45532 8.99245 6.50752 9.00737 6.56303 9.0198L6.79173 9.07449C6.90276 9.09934 7.00468 9.13249 7.09748 9.17392C7.19028 9.21535 7.27066 9.26631 7.3386 9.32679C7.40655 9.38728 7.45916 9.45854 7.49645 9.54057C7.53457 9.6226 7.55404 9.71665 7.55487 9.82271C7.55404 9.97849 7.51427 10.1135 7.43555 10.2279C7.35766 10.3414 7.24497 10.4297 7.09748 10.4926C6.95082 10.5548 6.77391 10.5858 6.56676 10.5858C6.36127 10.5858 6.18229 10.5544 6.02983 10.4914C5.8782 10.4284 5.75971 10.3352 5.67436 10.2117C5.58985 10.0874 5.54552 9.93374 5.54137 9.75062H6.06215C6.06795 9.83597 6.09239 9.90723 6.13548 9.9644C6.17939 10.0207 6.23781 10.0634 6.31073 10.0924C6.38447 10.1206 6.46775 10.1347 6.56055 10.1347C6.65169 10.1347 6.73083 10.1214 6.79794 10.0949C6.86589 10.0684 6.9185 10.0315 6.95579 9.98429C6.99308 9.93706 7.01172 9.88278 7.01172 9.82147C7.01172 9.76429 6.99473 9.71623 6.96076 9.67729C6.92762 9.63835 6.87873 9.6052 6.8141 9.57786C6.7503 9.55051 6.672 9.52566 6.57919 9.50328L6.30203 9.43368C6.08742 9.38148 5.91797 9.29986 5.79368 9.18883C5.66939 9.0778 5.60766 8.92824 5.60849 8.74015C5.60766 8.58603 5.64868 8.45138 5.73154 8.3362C5.81522 8.22103 5.92999 8.13113 6.07582 8.06649C6.22165 8.00186 6.38737 7.96955 6.57298 7.96955C6.7619 7.96955 6.92679 8.00186 7.06765 8.06649C7.20934 8.13113 7.31955 8.22103 7.39826 8.3362C7.47698 8.45138 7.51758 8.58478 7.52007 8.73642H7.00426Z"
      fill="white"
    />
    <defs>
      <clipPath id="clip0_12315_45386">
        <rect width={12} height={14} fill="white" transform="translate(2)" />
      </clipPath>
    </defs>
  </svg>
);

export const CsvIcon = ({ className }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_12321_45307)">
      <path
        d="M2.71289 1.4002C2.71289 1.09851 2.83273 0.809184 3.04606 0.595861C3.25938 0.382539 3.54871 0.262695 3.85039 0.262695H9.45039C9.49274 0.262695 9.53369 0.279495 9.56344 0.309595L13.341 4.08715C13.3709 4.11719 13.3877 4.15781 13.3879 4.2002V12.6002C13.3879 12.9019 13.268 13.1912 13.0547 13.4045C12.8414 13.6179 12.5521 13.7377 12.2504 13.7377H3.85039C3.54871 13.7377 3.25938 13.6179 3.04606 13.4045C2.83273 13.1912 2.71289 12.9019 2.71289 12.6002V1.4002Z"
        fill="white"
        stroke="#D5D7DA"
        strokeWidth={0.525}
      />
      <path
        d="M9.4502 0.174805V2.7998C9.4502 3.17111 9.5977 3.5272 9.86025 3.78975C10.1228 4.05231 10.4789 4.1998 10.8502 4.1998H13.4752"
        stroke="#D5D7DA"
        strokeWidth={0.525}
      />
      <path
        d="M9.44961 6.2998H1.04961C0.66301 6.2998 0.349609 6.61321 0.349609 6.9998V11.1998C0.349609 11.5864 0.66301 11.8998 1.04961 11.8998H9.44961C9.83621 11.8998 10.1496 11.5864 10.1496 11.1998V6.9998C10.1496 6.61321 9.83621 6.2998 9.44961 6.2998Z"
        fill="#079455"
      />
      <path
        d="M3.94505 8.84581H3.40045C3.39218 8.77999 3.37152 8.71634 3.33955 8.65821C3.30956 8.60392 3.26953 8.55582 3.2216 8.51646C3.17192 8.47688 3.11547 8.44664 3.055 8.42721C2.98962 8.40572 2.92116 8.39507 2.85235 8.39571C2.72075 8.39571 2.60606 8.42861 2.5083 8.49441C2.4103 8.55881 2.33446 8.65331 2.2808 8.77791C2.2269 8.90111 2.19995 9.05103 2.19995 9.22766C2.19995 9.40896 2.22678 9.56133 2.28045 9.68476C2.33551 9.80843 2.41181 9.90176 2.50935 9.96476C2.60735 10.0278 2.72051 10.0593 2.84885 10.0593C2.92095 10.0593 2.98756 10.0497 3.0487 10.0306C3.111 10.0114 3.16618 9.98366 3.21425 9.94726C3.26195 9.90986 3.3023 9.86394 3.33325 9.81181C3.36591 9.75861 3.38831 9.69806 3.40045 9.63016L3.94505 9.63261C3.93105 9.74951 3.89581 9.86221 3.83935 9.97071C3.78256 10.0802 3.7063 10.1784 3.6143 10.2605C3.51601 10.348 3.40215 10.4163 3.27865 10.4618C3.14938 10.5108 3.0032 10.5353 2.8401 10.5353C2.61306 10.5353 2.41006 10.4839 2.2311 10.3813C2.05041 10.2758 1.9037 10.1208 1.8083 9.93466C1.70563 9.74006 1.6543 9.5044 1.6543 9.22766C1.6543 8.95 1.70645 8.71386 1.81075 8.51926C1.91528 8.32443 2.05703 8.17603 2.236 8.07406C2.4201 7.96996 2.62862 7.9168 2.8401 7.92006C2.98756 7.92006 3.1243 7.94071 3.2503 7.98201C3.377 8.02378 3.48923 8.08433 3.587 8.16366C3.685 8.2423 3.76456 8.33878 3.8257 8.45311C3.88753 8.56745 3.92755 8.69835 3.94505 8.84581ZM5.7115 8.68691C5.70854 8.64095 5.69553 8.59619 5.6734 8.55579C5.65127 8.5154 5.62054 8.48035 5.5834 8.45311C5.5078 8.39758 5.40548 8.36981 5.27645 8.36981C5.18848 8.36981 5.11428 8.3823 5.05385 8.40726C4.99896 8.4273 4.95073 8.4622 4.91455 8.50806C4.88344 8.55059 4.86687 8.60202 4.8673 8.65471C4.86483 8.69635 4.87482 8.73778 4.896 8.77371C4.91879 8.80914 4.94886 8.83932 4.9842 8.86226C5.02153 8.8863 5.0647 8.9073 5.1137 8.92526C5.1627 8.94276 5.21485 8.95781 5.27015 8.97041L5.4987 9.02501C5.61 9.04975 5.71196 9.08288 5.8046 9.12441C5.89746 9.16571 5.97785 9.2167 6.04575 9.27736C6.11365 9.3378 6.16615 9.40896 6.20325 9.49086C6.24151 9.57323 6.26111 9.66738 6.26205 9.77331C6.26111 9.92895 6.22133 10.0639 6.1427 10.1783C6.06453 10.2919 5.95183 10.3802 5.8046 10.4432C5.6576 10.5053 5.48073 10.5363 5.274 10.5363C5.0682 10.5363 4.88923 10.5048 4.7371 10.4418C4.59376 10.3854 4.47013 10.2882 4.3815 10.1622C4.29703 10.0378 4.2527 9.88415 4.2485 9.70121H4.7693C4.7749 9.78661 4.7994 9.85778 4.8428 9.91471C4.88666 9.97118 4.945 10.0139 5.0178 10.0428C5.09153 10.0708 5.17483 10.0848 5.2677 10.0848C5.3587 10.0848 5.4378 10.0716 5.505 10.0453C5.5666 10.0233 5.62112 9.98504 5.66285 9.93466C5.69977 9.88852 5.71956 9.83101 5.71885 9.77191C5.71885 9.71498 5.70181 9.66691 5.66775 9.62771C5.62725 9.58363 5.57704 9.5496 5.5211 9.52831C5.44527 9.49663 5.3668 9.47168 5.2866 9.45376L5.00905 9.38411C4.79438 9.33185 4.62498 9.25018 4.50085 9.13911C4.37648 9.02828 4.31465 8.87871 4.31535 8.69041C4.31465 8.53641 4.35571 8.40178 4.43855 8.28651C4.52255 8.17148 4.63735 8.08165 4.78295 8.01701C4.92878 7.95238 5.09445 7.92006 5.27995 7.92006C5.46895 7.92006 5.63391 7.95238 5.77485 8.01701C5.91671 8.08141 6.02685 8.17125 6.10525 8.28651C6.18411 8.40178 6.22471 8.53525 6.22705 8.68691H5.7115ZM7.0835 7.95471L7.6995 9.88881H7.72295L8.33965 7.95471H8.93605L8.0586 10.5003H7.3649L6.4864 7.95471H7.0835Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_12321_45307">
        <rect width={14} height={14} fill="white" />
      </clipPath>
    </defs>
  </svg>
);
